import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../shadcn/components/ui/Card';
import { Button } from '../../../shadcn/components/ui/Button';
import { Input } from '../../../shadcn/components/ui/Input';
import { Alert, AlertDescription, AlertTitle } from '../../../shadcn/components/ui/Alert';

import { Separator } from '../../../shadcn/components/ui/Separator';
import {
    UploadIcon,
    DownloadIcon,
    CheckCircleIcon,
    XCircleIcon,
    AlertTriangleIcon,
    FileTextIcon,
    RefreshCwIcon
} from 'lucide-react';
import { opsVisibilityApi as api } from '../../../api/opsVisibility';

function OutOfBoxAvailabilityUpload() {
    const [file, setFile] = useState(null);
    const [uploadResults, setUploadResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const fileInputRef = useRef(null);

    const columns = [
        { header: 'External SKU', accessor: 'externalSku' },
        { header: 'Zip Code', accessor: 'zipCode' },
        { header: 'Line Haul ID', accessor: 'lineHaulId' },
        { header: 'Action', accessor: 'action' },
        { header: 'Errors', accessor: 'errors' },
        { header: 'Warnings', accessor: 'warnings' },
    ];

    const handleFileSelect = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setUploadResults(null);
        }
    };

    const uploadFile = async () => {
        if (!file) return;

        try {
            setLoading(true);
            const formData = new FormData();
            formData.append('availabilityFile', file);

            const response = await api.uploadOutOfBoxAvailability(formData);

            if (response.status) {
                // Success case - no validation errors
                const data = response.data;
                const results = data.results
                    .map((row) => ({
                        ...row,
                        errors: row.errors?.join('. ') || '',
                        warnings: row.warnings?.join('. ') || '',
                    }));

                setUploadResults({
                    ...data,
                    results,
                    success: true,
                });
            } else if (response.data && response.data.results) {
                // Validation errors case - still show results for user to review
                const data = response.data;
                const results = data.results
                    .map((row) => ({
                        ...row,
                        errors: row.errors?.join('. ') || '',
                        warnings: row.warnings?.join('. ') || '',
                    }));

                setUploadResults({
                    ...data,
                    results,
                    success: false,
                    message: response.msg || 'Validation errors found',
                });
            } else {
                // True error case - no data to show
                setUploadResults({
                    error: true,
                    message: response.msg || 'Upload failed',
                });
            }
        } catch (error) {
            console.error('Upload error:', error);
            setUploadResults({
                error: true,
                message: 'An error occurred during upload',
            });
        } finally {
            setLoading(false);
        }
    };

    const confirmUpload = async () => {
        if (!uploadResults?.key) return;

        try {
            setConfirming(true);
            const response = await api.confirmOutOfBoxAvailabilityUpload(uploadResults.key);

            if (response.status) {
                // Success - reset form
                setFile(null);
                setUploadResults(null);
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }

                // Show success message (you might want to add a toast notification here)
                setUploadResults({
                    success: true,
                    message: 'Upload confirmed successfully!',
                });
            } else {
                setUploadResults({
                    ...uploadResults,
                    error: true,
                    message: response.msg || 'Confirmation failed',
                });
            }
        } catch (error) {
            console.error('Confirmation error:', error);
            setUploadResults({
                ...uploadResults,
                error: true,
                message: 'An error occurred during confirmation',
            });
        } finally {
            setConfirming(false);
        }
    };

    const downloadTemplate = () => {
        const csvContent = 'data:text/csv;charset=utf-8,external_sku,zip_code,line_haul_id,action\n118601,12345,481,ADD\n118602,67890,823,REMOVE';
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', 'out_of_box_availability_template.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const clearUpload = () => {
        setFile(null);
        setUploadResults(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const hasErrors = uploadResults?.results?.some(row => row.errors && row.errors.length > 0);
    const hasWarnings = uploadResults?.results?.some(row => row.warnings && row.warnings.length > 0);

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                        <UploadIcon className="h-5 w-5" />
                        <span>Bulk Upload Out of Box Availability</span>
                    </CardTitle>
                    <p className="text-sm text-gray-600">
                        Upload a CSV file to bulk update out of box availability data. Use "ADD" to create new records or "REMOVE" to delete existing ones.
                    </p>
                </CardHeader>

                <CardContent className="space-y-4">
                    {!uploadResults && (
                        <>
                            <div className="flex space-x-2">
                                <Button
                                    onClick={downloadTemplate}
                                    variant="outline"
                                    className="flex items-center space-x-2"
                                >
                                    <DownloadIcon className="h-4 w-4" />
                                    <span>Download Template</span>
                                </Button>
                            </div>

                            <Separator />

                            <div className="space-y-2">
                                <label htmlFor="file-upload" className="text-sm font-medium">
                                    Select CSV File
                                </label>
                                <Input
                                    ref={fileInputRef}
                                    id="file-upload"
                                    type="file"
                                    accept=".csv"
                                    onChange={handleFileSelect}
                                    className="cursor-pointer"
                                />
                                {file && (
                                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                                        <FileTextIcon className="h-4 w-4" />
                                        <span>{file.name}</span>
                                        <span>({(file.size / 1024).toFixed(1)} KB)</span>
                                    </div>
                                )}
                            </div>

                            <Button
                                onClick={uploadFile}
                                disabled={!file || loading}
                                className="w-full"
                            >
                                {loading ? (
                                    <>
                                        <RefreshCwIcon className="h-4 w-4 mr-2 animate-spin" />
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        <UploadIcon className="h-4 w-4 mr-2" />
                                        Upload & Review
                                    </>
                                )}
                            </Button>
                        </>
                    )}

                    {uploadResults && (
                        // Determine overall status based on presence of errors / warnings / success flags
                        (() => {
                            const hasErrors = uploadResults?.results?.some(row => row.errors && row.errors.length > 0);
                            const hasWarnings = uploadResults?.results?.some(row => row.warnings && row.warnings.length > 0);
                            let status;
                            if (uploadResults.error || hasErrors) {
                                status = 'error';
                            } else if (hasWarnings) {
                                status = 'warning';
                            } else if (uploadResults.success) {
                                status = 'success';
                            }

                            const alertComponent = (() => {
                                if (status === 'error') {
                                    return (
                                        <Alert variant="destructive">
                                            <XCircleIcon className="h-4 w-4" />
                                            <AlertTitle>Error</AlertTitle>
                                            <AlertDescription>
                                                {uploadResults.message || 'There are errors in the uploaded file. Please review and correct them before proceeding.'}
                                            </AlertDescription>
                                        </Alert>
                                    );
                                }
                                if (status === 'warning') {
                                    return (
                                        <Alert className="border-yellow-200 bg-yellow-50 text-yellow-800">
                                            <AlertTriangleIcon className="h-4 w-4" />
                                            <AlertTitle>Success with Warnings</AlertTitle>
                                            <AlertDescription>
                                                Some warnings were found. Please review them before confirming.
                                            </AlertDescription>
                                        </Alert>
                                    );
                                }
                                if (status === 'success') {
                                    return (
                                        <Alert className="border-green-200 bg-green-50 text-green-800">
                                            <CheckCircleIcon className="h-4 w-4" />
                                            <AlertTitle>Success</AlertTitle>
                                            <AlertDescription>
                                                {uploadResults.message || 'All data validated successfully. Please review and confirm the upload.'}
                                            </AlertDescription>
                                        </Alert>
                                    );
                                }
                                return null;
                            })();

                            return (
                                <div className="space-y-4">
                                    {alertComponent}
                                    <div className="flex space-x-2">
                                        <Button onClick={clearUpload} variant="outline">
                                            Clear File
                                        </Button>
                                        {!hasErrors && !uploadResults.error && uploadResults.key !== null && (
                                            <Button
                                                onClick={confirmUpload}
                                                disabled={confirming}
                                                className="flex items-center space-x-2"
                                            >
                                                {confirming ? (
                                                    <>
                                                        <RefreshCwIcon className="h-4 w-4 animate-spin" />
                                                        <span>Confirming...</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <CheckCircleIcon className="h-4 w-4" />
                                                        <span>Confirm Upload</span>
                                                    </>
                                                )}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            );
                        })()
                    )}
                </CardContent>
            </Card>

            {uploadResults?.results && uploadResults.results.length > 0 && !uploadResults.error && (
                <Card>
                    <CardHeader>
                        <CardTitle>Upload Results</CardTitle>
                        <p className="text-sm text-gray-600">
                            {uploadResults.results.length} rows processed
                        </p>
                    </CardHeader>

                    <CardContent>
                        <div className="rounded-md border">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b bg-gray-50">
                                            {columns.map((column) => (
                                                <th
                                                    key={column.accessor}
                                                    className="px-4 py-3 text-left text-sm font-medium text-gray-900"
                                                >
                                                    {column.header}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {uploadResults.results.map((row, index) => (
                                            <tr
                                                key={index}
                                                className={`border-b ${row.errors ? 'bg-red-50' :
                                                        row.warnings ? 'bg-yellow-50' :
                                                            'hover:bg-gray-50'
                                                    }`}
                                            >
                                                {columns.map((column) => (
                                                    <td key={column.accessor} className="px-4 py-3 text-sm">
                                                        <span className={
                                                            column.accessor === 'errors' && row[column.accessor] ? 'text-red-600' :
                                                                column.accessor === 'warnings' && row[column.accessor] ? 'text-yellow-600' :
                                                                    column.accessor === 'action' && row[column.accessor] === 'ADD' ? 'text-green-600 font-semibold' :
                                                                        column.accessor === 'action' && row[column.accessor] === 'REMOVE' ? 'text-red-600 font-semibold' :
                                                                            column.accessor === 'zipCode' || column.accessor === 'externalSku' || column.accessor === 'lineHaulId' ? 'font-mono' :
                                                                                ''
                                                        }>
                                                            {row[column.accessor] || ''}
                                                        </span>
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}

export { OutOfBoxAvailabilityUpload };